// src/contexts/UserContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { useError } from './ErrorContext';
import { useAuth } from './AuthContext';

const UserContext = createContext();

export function UserProvider({ children }) {
  const [userState, setUserState] = useState({ userId: '', userName: '', userEmail: '' });
  const [error, setError] = useState(null);

  // Get error context for global error handling
  const { addError } = useError();

  // Get auth context to check if user is authenticated
  const { authState } = useAuth();

  // Only load from localStorage if user is authenticated or in signup flow
  useEffect(() => {
    const isSignupFlowActive = localStorage.getItem('signupInProgress') === 'true';

    if (authState.isAuthenticated || isSignupFlowActive) {
      const savedState = localStorage.getItem('userState');
      if (savedState) {
        try {
          const parsedState = JSON.parse(savedState);
          setUserState(parsedState);
        } catch (error) {
          console.error('Error parsing userState from localStorage:', error);
          setUserState({ userId: '', userName: '', userEmail: '' });
        }
      }
    } else {
      // Clear user state if not authenticated and not in signup flow
      setUserState({ userId: '', userName: '', userEmail: '' });
    }
  }, [authState.isAuthenticated]);

  // Enhanced error handling for user operations
  const handleUserError = (error, operation = 'user-operation') => {
    console.error('User operation error:', error);

    // Only show user-relevant errors, log technical errors
    if (error.message?.includes('validation') ||
      error.message?.includes('must be') ||
      error.message?.includes('Invalid email') ||
      error.message?.includes('permission') ||
      error.message?.includes('session')) {
      addError(error, {
        source: 'user',
        type: operation,
        retryable: false,
        autoDismiss: true
      });
    }

    setError(error.message || 'An error occurred while updating user data');
  };

  // Clear error after timeout
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Save to localStorage with error handling
  useEffect(() => {
    try {
      localStorage.setItem('userState', JSON.stringify(userState));
    } catch (error) {
      handleUserError(error, 'user-storage');
    }
  }, [userState]);

  // Function to clear error
  const clearError = () => {
    setError(null);
  };

  // Function to clear user state and localStorage
  const clearUserState = () => {
    try {
      setUserState({ userId: '', userName: '', userEmail: '' });
      localStorage.removeItem('userState');
      setError(null);
    } catch (error) {
      handleUserError(error, 'user-state-clear');
    }
  };

  // Setter functions with error handling
  const setUserId = (id) => {
    try {
      if (typeof id !== 'string') {
        throw new Error('User ID must be a string');
      }
      setUserState(prevState => ({ ...prevState, userId: id }));
      setError(null); // Clear any existing errors on successful update
    } catch (error) {
      handleUserError(error, 'user-id-update');
    }
  };

  const setUserName = (name) => {
    try {
      if (typeof name !== 'string') {
        throw new Error('User name must be a string');
      }
      setUserState(prevState => ({ ...prevState, userName: name }));
      setError(null); // Clear any existing errors on successful update
    } catch (error) {
      handleUserError(error, 'user-name-update');
    }
  };

  const setUserEmail = (email) => {
    try {
      if (typeof email !== 'string') {
        throw new Error('User email must be a string');
      }
      // Basic email validation
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        throw new Error('Invalid email format');
      }
      setUserState(prevState => ({ ...prevState, userEmail: email }));
      setError(null); // Clear any existing errors on successful update
    } catch (error) {
      handleUserError(error, 'user-email-update');
    }
  };

  const setUserPassword = (password) => {
    try {
      if (typeof password !== 'string') {
        throw new Error('User password must be a string');
      }
      setUserState(prevState => ({ ...prevState, userPassword: password }));
      setError(null); // Clear any existing errors on successful update
    } catch (error) {
      handleUserError(error, 'user-password-update');
    }
  };

  return (
    <UserContext.Provider
      value={{
        userState,
        error,
        setUserId,
        setUserName,
        setUserEmail,
        setUserPassword,
        clearError,
        clearUserState
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUserContext() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
}
